import { useCallback, useMemo, useState } from "react";

import { Box, Flex, Typography, Menu, ItemElementProps } from "@nestoca/ui";
import { useGetAccount, useMutateAccount } from "@shared/api/hooks/account";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { toast } from "react-toastify";

import styles from "./burger-menu.module.scss";

type LangId = "en" | "fr";

export const LanguageSelector = ({
  changePreferredLanguage = true,
}: {
  changePreferredLanguage?: boolean;
}) => {
  const { t } = useTranslation("common");
  const { locale, asPath, replace } = useRouter();
  const [language, setLanguage] = useState(locale);
  const selectedLanguage =
    language === "en"
      ? t("common:language.english")
      : t("common:language.french");

  const { data: account } = useGetAccount();
  const accountMutation = useMutateAccount();

  const onOptionClick = useCallback(
    (selectedLanguage: LangId) => {
      setLanguage(selectedLanguage);

      if (!changePreferredLanguage) {
        return replace(asPath, asPath, {
          locale: selectedLanguage,
        });
      }

      if (!account) {
        return toast(t("failedToSave", { ns: "error" }), { type: "error" });
      }

      accountMutation.mutate(
        { ...account, preferredLanguage: selectedLanguage },
        {
          onSuccess: () => {
            replace(asPath, asPath, {
              locale: selectedLanguage,
            });
          },
          onError: () => {
            toast(t("dataUpdatedFailure", { ns: "applications" }), {
              type: "error",
            });
          },
        }
      );
    },
    [account, asPath, replace, t, accountMutation, changePreferredLanguage]
  );

  const options = useMemo<ItemElementProps[]>(
    () => [
      {
        id: "en",
        key: "en",
        title: t("common:language.english"),
        isSelected: language === "en",
        onClick: () => {
          onOptionClick("en");
        },
      },
      {
        id: "fr",
        key: "fr",
        title: t("common:language.french"),
        isSelected: language === "fr",
        onClick: () => {
          onOptionClick("fr");
        },
      },
    ],
    [language, t, onOptionClick]
  );

  const LanguageButton = (isMenuOpen: boolean | undefined) => (
    <Flex className={styles["language-button"]} gap={4}>
      <Box className={styles["language-button-name"]}>
        {t(selectedLanguage)}
      </Box>
      {isMenuOpen ? (
        <BsChevronUp size="1rem" color={"var(--color-brand)"} />
      ) : (
        <BsChevronDown size="1rem" color={"var(--color-brand)"} />
      )}
    </Flex>
  );

  return (
    <Flex align="center" justify="between" className={styles["language"]}>
      <Typography size={0} weight={5}>
        {t("language.label")}
      </Typography>
      <Menu
        menuItems={[
          {
            sectionAtoms: options,
          },
        ]}
        selectionMode="single"
        buttonChildren={LanguageButton}
      />
    </Flex>
  );
};
